/**
 * Which of a table's columns a column name refers to — without folding two
 * columns into one. A column's exact name is its identity: `"ref-id"` and
 * `"ref id"`, or PostgreSQL's `"Ref"` and `ref`, are two columns, and each
 * keeps its own FK target. A reference spelled another way (a catalog or
 * driver reporting another case, a MetaInfo declaration typed by hand) still
 * finds its column when it names exactly one of them ignoring case — never
 * when that match is ambiguous. `normalizeColumnName` (test ids, loose
 * display matching) must never key column identity.
 */

const lower = (name: string): string => name.toLowerCase();

/**
 * The spelling among `names` that `name` refers to: `name` itself when listed,
 * else the one listed name equal to it under `fold` (case-insensitive by
 * default); undefined when none or several are.
 */
export function matchColumnName(
  name: string,
  names: Iterable<string>,
  fold: (value: string) => string = lower,
): string | undefined {
  const key = fold(name);
  let match: string | undefined;
  let ambiguous = false;
  for (const candidate of names) {
    if (candidate === name) return candidate;
    if (fold(candidate) !== key) continue;
    if (match === undefined) match = candidate;
    else if (match !== candidate) ambiguous = true;
  }
  return ambiguous ? undefined : match;
}

/** The column `name` refers to among `columns` (`matchColumnName` over their names). */
export function findColumnByName<T extends { readonly name: string }>(columns: readonly T[], name: string): T | undefined {
  const match = matchColumnName(name, columns.map((c) => c.name));
  return match === undefined ? undefined : columns.find((c) => c.name === match);
}
